/**
 * The follow star, presentational only: no hooks and no SDK reads. The
 * standings row star (`TeamFollowStar`) and the team header star
 * (`TeamHeader`) both render it, so the two can never look or act
 * differently (ISSUE-1082).
 *
 * State is never color alone: the accessible name changes ("Follow" versus
 * "Following") and the glyph fills, so a fan who cannot see color still gets
 * the state.
 *
 * While a follow write is in flight the star stays focusable: it carries
 * `aria-disabled` and `aria-busy` and refuses the press in its handler, never
 * the `disabled` attribute, per the rule on `BUSY_CONTROL` in
 * `packages/ui/src/lib/focus.ts`.
 *
 * The handler prevents the default and stops propagation because a standings
 * row's link carries a full row click overlay underneath the star.
 */
import { BUSY_CONTROL, cn } from "@sportsmet/ui";
import type { ReactNode } from "react";

import { ICON_BUTTON_CLASS, StarIcon } from "../../icons/actionIcons";

export const TEAM_FOLLOW_STAR_FOLLOW_LABEL = (teamName: string): string =>
  `Follow ${teamName}`;
export const TEAM_FOLLOW_STAR_FOLLOWING_LABEL = (teamName: string): string =>
  `Following ${teamName}`;

export type typeTeamFollowStarButtonProps = {
  teamName: string;
  isFollowing: boolean;
  /** A follow write is in flight. The star stays focusable and refuses the press. */
  isPending: boolean;
  onToggle: () => void;
  testId: string;
  /** Placement classes, e.g. "relative z-10" over a standings row's link overlay. */
  className?: string;
};

export const TeamFollowStarButton = (
  props: typeTeamFollowStarButtonProps,
): ReactNode => (
  <button
    type="button"
    data-testid={props.testId}
    data-following={props.isFollowing ? "true" : undefined}
    aria-pressed={props.isFollowing}
    aria-label={
      props.isFollowing
        ? TEAM_FOLLOW_STAR_FOLLOWING_LABEL(props.teamName)
        : TEAM_FOLLOW_STAR_FOLLOW_LABEL(props.teamName)
    }
    aria-disabled={props.isPending || undefined}
    aria-busy={props.isPending || undefined}
    className={cn(
      ICON_BUTTON_CLASS,
      props.isFollowing ? "text-primary" : "text-ink-tertiary",
      BUSY_CONTROL,
      props.className,
    )}
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      if (props.isPending) return;
      props.onToggle();
    }}
  >
    <StarIcon isFilled={props.isFollowing} />
  </button>
);
